<template>
  <v-navigation-drawer
    :rail="!isExpanded"
    :rail-width="72"
    :width="240"
    permanent
    class="app-sidebar"
    :class="{ 'app-sidebar--expanded': isExpanded }">
    <!-- Logo -->
    <div class="app-sidebar__logo" @click="goHome">
      <img
        v-if="isExpanded"
        src="@/assets/images/header-logo-white.png"
        alt="Lamar Health"
        class="app-sidebar__logo-full" />
      <img v-else src="@/assets/images/header-logo-white-icon.png" alt="LH" class="app-sidebar__logo-icon" />
    </div>

    <!-- Nav items -->
    <div class="app-sidebar__nav">
      <SidebarNavItem icon="mdi-tray-arrow-down" label="Intake" route="intake" :expanded="isExpanded" />
      <SidebarNavItem
        icon="mdi-file-document-check-outline"
        label="Prior Auth"
        route="prior-auth"
        :expanded="isExpanded" />
      <SidebarNavItem
        v-if="cmmEnabled"
        icon="mdi-import"
        label="Import CMM"
        route="import-cmm-submission"
        :expanded="isExpanded" />
    </div>

    <!-- Footer -->
    <template #append>
      <div class="app-sidebar__footer">
        <SidebarNavItem icon="mdi-cog-outline" label="Settings" :expanded="isExpanded" @click="openSettings" />
        <SidebarNavItem icon="mdi-logout" label="Log Out" :expanded="isExpanded" @click="logout" />
        <button class="app-sidebar__toggle" @click="toggleExpanded">
          <v-icon :icon="isExpanded ? 'mdi-chevron-left' : 'mdi-chevron-right'" size="18" />
        </button>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore, useRequirementsStore, useTableSettingsStore } from "@/stores";
import SidebarNavItem from "@/components/SidebarNavItem.vue";
import { SCOPE } from "@/constants/scope";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const requirementsStore = useRequirementsStore();
const tableSettingsStore = useTableSettingsStore();

const STORAGE_KEY = "lh_sidebar_expanded";
const isExpanded = ref(localStorage.getItem(STORAGE_KEY) === "true");

const cmmEnabled = computed(() => {
  return requirementsStore.clientConfig?.priorAuth?.cmmIntakeEnabled ?? false;
});

onMounted(async () => {
  if (!requirementsStore.clientConfig) {
    await requirementsStore.refreshCurrentUserClientConfig();
  }
});

function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
  localStorage.setItem(STORAGE_KEY, String(isExpanded.value));
}

function goHome() {
  const page = requirementsStore.clientConfig?.defaultPostLoginPage || "prior-auth";
  router.push({ name: page });
}

// Map route name → settings scope so opening Settings from a given table
// preselects the matching tab. Legacy route names (all-patients / all-submissions)
// are kept alongside the new dashboard names.
const ROUTE_SCOPE = Object.freeze({
  intake: SCOPE.INTAKE,
  "all-patients": SCOPE.INTAKE,
  "prior-auth": SCOPE.PA,
  "all-submissions": SCOPE.PA,
});

function openSettings() {
  tableSettingsStore.open(ROUTE_SCOPE[route.name]);
}

async function logout() {
  await authStore.logout();
  window.location.reload();
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.app-sidebar {
  background: $lh_sidebar_bg !important;
  border-right: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.app-sidebar__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 12px;
  cursor: pointer;
  min-height: 64px;
}

.app-sidebar__logo-full {
  height: 28px;
  width: auto;
  object-fit: contain;
}

.app-sidebar__logo-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.app-sidebar__nav {
  flex: 1;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.app-sidebar__footer {
  padding: 8px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-top: 1px solid rgba(white, 0.08);
}

.app-sidebar__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 0;
  margin-top: 4px;
  border: none;
  background: transparent;
  color: $lh_sidebar_text;
  cursor: pointer;
  border-radius: 8px;
  transition: color $lh_transition_fast, background-color $lh_transition_fast;

  &:hover {
    color: $lh_sidebar_text_active;
    background: $lh_sidebar_bg_hover;
  }
}
</style>
